import { useMutation } from "@tanstack/react-query";
import {
	CreateUpcomingOnlineCourseDto,
	OnlineCourseStatus,
	restRepository,
} from "@onair/repositories";
import { AdminOnlineCourseService } from "@onair/services";
import { useStoreCourseUpsert } from "@onair/store/online-course";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";

export const useHostPublishedOnlineCourseByIdMutation = (options = {}) => {
	const setPreventDraft = useStoreCourseUpsert(
		(state) => state.setPreventDraft,
	);
	return useMutation({
		...options,
		mutationFn: async (id: string) => {
			const response = await restRepository.api.onlineCourseHostPublished(id);
			return response.data;
		},
		onMutate: () => {
			setPreventDraft(true);
		},
	});
};

const removeEmptyBenefitArchivedValue = (value?: string | null) => {
	if (!value) return "[]";
	const parsedValue = JSON.parse(value);
	return JSON.stringify(
		parsedValue.filter((item: { name: string }) => item.name),
	);
};

const removeEmptyQuestionAndAnswerValue = (value?: string | null) => {
	if (!value) return "[]";
	const parsedValue = JSON.parse(value);
	return JSON.stringify(
		parsedValue.filter(
			(item: { question: string; answer: string }) =>
				item.question || item.answer,
		),
	);
};

export const useCreateUpcomingOnlineCourseMutation = (options = {}) => {
	return useMutation({
		...options,
		mutationFn: async (data: CreateUpcomingOnlineCourseDto) => {
			return await AdminOnlineCourseService.createUpcomingCourse(data);
		},
	});
};

export const useUpdateUpcomingOnlineCourseMutation = (options = {}) => {
	return useMutation({
		...options,
		mutationFn: async (
			data: CreateUpcomingOnlineCourseDto & { id: string },
		) => {
			return await AdminOnlineCourseService.updateUpcomingCourse(
				data.id,
				omit(data, ["id"]),
			);
		},
	});
};

export const useMutationCourseSaveDraft = (options = {}) => {
	// IMPORTANT: after save draft, neeed to clear changes/news/deleteds

	const {
		preventDraft,
		setIsSaving,
		resetChangeCourse,
		resetChangeProduct,
		resetChangeSection,
		resetChangeLesson,
		changes,
		deleteSections,
		course,
		deleteResources,
		deleteLessons,
		resetDeleteResources,
		updateProductFromAPI,
	} = useStoreCourseUpsert((state) => {
		return {
			preventDraft: state.preventDraft,
			deleteResources: state.deleteLessonResources,
			course: state.course,
			deleteSections: state.deleteSections,
			deleteLessons: state.deleteLessons,
			setIsSaving: state.setIsSaving,
			existingCourse: state.course,
			resetChangeCourse: state.resetChangeCourse,
			changes: state.changes,
			resetChangeSection: state.resetChangeSection,
			resetChangeProduct: state.resetChangeProduct,
			resetChangeLesson: state.resetChangeLesson,
			resetDeleteResources: state.resetDeleteLessonResources,
			updateProductFromAPI: state.updateProductFromAPI,
		};
	});

	return useMutation({
		...options,
		mutationFn: async () => {
			console.log("preventDraft", preventDraft, deleteLessons);
			if (preventDraft) return;
			setIsSaving(true);

			if (
				isEmpty(changes.course) &&
				isEmpty(changes.product) &&
				isEmpty(changes.section) &&
				isEmpty(changes.lesson) &&
				isEmpty(deleteLessons) &&
				isEmpty(deleteSections) &&
				isEmpty(deleteResources)
			) {
				setIsSaving(false);
				return;
			}
			// IMPORTANT order
			// 1. promise course, course need to init id by 	 if not exist
			// 2. promise section, section need to init id by cuid() if not exist
			// 3. promise lesson
			try {
				if (!isEmpty(changes.course) || !isEmpty(changes.product)) {
					try {
						console.log("CHANGEEEE COURSE", changes);

						let extraInformation = {};
						let productData = {};

						if (!isEmpty(changes.course)) {
							extraInformation = {
								benefit: removeEmptyBenefitArchivedValue(
									changes?.course?.benefit,
								),
								archived: removeEmptyBenefitArchivedValue(
									changes?.course?.archived,
								),
								questionAndAnswers: removeEmptyQuestionAndAnswerValue(
									changes?.course?.questionAndAnswers,
								),
							};
						}

						if (!isEmpty(changes.product)) {
							productData = {
								price: changes.product.price,
								availableDays: changes.product.availableDays,
								pricingPlan: changes.product.pricingPlan,
								isAcademy: changes.product.isAcademy,
								isReskill: changes.product.isReskill,
								isLaunchpad: changes.product.isLaunchpad,
								isOnair: typeof changes.product.isOnair === "undefined" ? true : changes.product.isOnair,
								ownerId: changes.product.ownerId || course?.product?.ownerId,
								socialCommunities: changes.product.socialCommunities,
								productSurveys: changes.product.productSurveys,
							};
						}

						const courseData = {
							...changes.course,
							id: course?.id,
							...extraInformation,
							...productData,
							status: changes.course?.status || OnlineCourseStatus.DRAFT,
						};
						console.log("COURSE DATA", courseData);
						delete courseData.sections;
						const courseRequest =
							await restRepository.api.onlineCourseUpsertByAdmin(courseData);
						// if dont have product and having after courseRequest, update to course state

						const { product, ...restCourse } = courseRequest.data;

						if (product) {
							updateProductFromAPI(product);
						}
						// clear changes course
					} catch (error) {
					} finally {
						resetChangeCourse();
						resetChangeProduct();
					}
				} else {
					// In case user didn't change any course information, just update status to draft
					if (course?.id) {
						await restRepository.api.onlineCourseUpsertByAdmin({
							id: course?.id,
							status: OnlineCourseStatus.DRAFT,
						});
					}
				}
				//#region delete section
				const sectionRequests = [];

				if (Object.keys(changes.section).length) {
					sectionRequests.push(
						...Object.values(changes.section).map((section) => {
							console.log({ section });
							return restRepository.api.adminUpsertSection(
								omit({ ...section, hostId: course?.hostId }, ["lessons"]),
							);
						}),
					);
				}

				if (deleteSections.length) {
					sectionRequests.push(
						...deleteSections.map((section) =>
							restRepository.api.adminDeleteSectionById(section),
						),
					);
				}

				if (sectionRequests.length) {
					await Promise.all(sectionRequests)
						.then(() => { })
						.catch((error) => {
							console.log(error, "ERROR: sectionRequests");
						})
						.finally(() => {
							resetChangeSection();
						});
				}
				//#endregion
				const lessonRequest = [];

				if (deleteLessons.length) {
					lessonRequest.push(
						...deleteLessons.map((lesson) =>
							restRepository.api.adminDeleteLesson(lesson),
						),
					);
				}
				console.log("deleteLessons LESSON", deleteLessons);
				if (Object.keys(changes.lesson).length) {
					lessonRequest.push(
						...Object.values(changes.lesson).map((lesson) => {
							return restRepository.api.adminUpsertLesson({
								...lesson,
								courseId: course?.id,
								hostId: course?.hostId,
							});
						}),
					);
				}
				if (lessonRequest.length) {
					await Promise.all(lessonRequest)
						.catch((error) => { })
						.finally(() => {
							resetChangeLesson();
						});
				}
				// delete resources

				if (deleteResources.length) {
					await Promise.all(
						deleteResources.map((resource) =>
							restRepository.api.adminRemoveResourceFromLessonByLessonId(
								resource.lessonId,
								resource.resourceId,
								{
									isMain: resource.isMain || false,
								},
							),
						),
					)
						.catch((error) => { })
						.finally(() => {
							// clear deleteResources
							resetDeleteResources();
						});
				}
			} finally {
				console.log("FINALLY");
				setIsSaving(false);
			}
		},
	});
};

export const useMutationCourseDelete = (options = {}) => {
	return useMutation({
		...options,
		mutationFn: async ({ id }: { id: string }) => {
			const response = await restRepository.api.onlineCourseDeleteById(id);
			return response.data;
		},
	});
};
